import { Injectable } from "@angular/core";
import { HttpService } from "./utils/http.service";

const AUTHENTICATED_LINK_CODES_KEY = "customerPortalAuthenticatedLinkCodes";

@Injectable()
export class CustomerPortalService {
    constructor(private httpService: HttpService) { }

    // ----- Self-service "forgot access" - re-sends a link back to the login page, never a
    // direct-access dashboard link. Dashboard access always requires a real login. -----

    async sendPortalLink(email: string): Promise<void> {
        return await this.httpService.post("/customerPortal/sendPortalLink", { email: email });
    }

    async sendPortalLinkByUsername(username: string): Promise<void> {
        return await this.httpService.post("/customerPortal/sendPortalLinkByUsername", { username: username });
    }

    // ----- Staff-invited login -----

    async getCreateLoginInfo(setupCode: string): Promise<any> {
        return await this.httpService.get("/customerPortal/getCreateLoginInfo", { setupCode: setupCode });
    }

    async createLogin(setupCode: string, username: string, password: string): Promise<{ linkCode: string }> {
        return await this.httpService.post("/customerPortal/createLogin", { setupCode: setupCode, username: username, password: password });
    }

    async login(username: string, password: string): Promise<any[]> {
        return await this.httpService.post("/customerPortal/login", { username: username, password: password });
    }

    // ----- Dashboard session gating -----
    //
    // linkCode in the URL is a bearer token for API calls, but by itself it must not be enough
    // to open the dashboard - only a linkCode just issued by login()/createLogin() in this
    // browser tab should unlock it. Tracked in sessionStorage so it clears when the tab closes
    // and never survives being emailed, bookmarked, or shared.

    // storage defaults to this tab's; staff opening the portal for a customer pass the new tab's.
    markSessionAuthenticated(linkCode: string, storage: Storage = sessionStorage): void {
        const codes = this.getAuthenticatedLinkCodes(storage);
        codes.add(linkCode);
        storage.setItem(AUTHENTICATED_LINK_CODES_KEY, JSON.stringify([...codes]));
    }

    isSessionAuthenticated(linkCode: string): boolean {
        return !!linkCode && this.getAuthenticatedLinkCodes().has(linkCode);
    }

    private getAuthenticatedLinkCodes(storage: Storage = sessionStorage): Set<string> {
        try {
            const raw = storage.getItem(AUTHENTICATED_LINK_CODES_KEY);
            return new Set(raw ? JSON.parse(raw) : []);
        } catch {
            return new Set();
        }
    }
}
